import {
  CheckIcon,
  CloseIcon,
  DeleteIcon,
  EditIcon,
  IconButton,
  TableCell,
  TableRow,
  Typography,
} from '@cmg/design-system';
import { FormikSelectField, FormikTextField } from '@cmg/design-system-formik';
import { Fragment } from 'react';

import { type Contact, STATUS_OPTIONS } from './FirmInvestorContactsPanel.model';

export type FirmInvestorContactRowProps = Readonly<{
  contact: Contact | null;
  isEditing: boolean;
  isSaving: boolean;
  canEdit: boolean;
  actionsDisabled: boolean;
  onEdit?: () => void;
  onDelete?: () => void;
  onSave: () => void;
  onCancel: () => void;
}>;

const getStatusLabel = (status: string) =>
  STATUS_OPTIONS.find(option => option.value === status)?.label ?? status;

export function FirmInvestorContactRow({
  contact,
  isEditing,
  isSaving,
  canEdit,
  actionsDisabled,
  onEdit,
  onDelete,
  onSave,
  onCancel,
}: FirmInvestorContactRowProps) {
  const isDraftRow = contact === null;

  return (
    <TableRow>
      <TableCell>
        {isEditing ? (
          <FormikTextField
            name="name"
            size="small"
            fullWidth
            hiddenLabel
            required
            showHelperTextInTooltip
            inputProps={{ 'aria-label': 'Full Name' }}
          />
        ) : (
          <Typography variant="body2">{contact?.name}</Typography>
        )}
      </TableCell>
      <TableCell>
        {isEditing ? (
          <FormikTextField
            name="email"
            size="small"
            fullWidth
            hiddenLabel
            required
            showHelperTextInTooltip
            inputProps={{ 'aria-label': 'Contact Email' }}
          />
        ) : (
          <Typography variant="body2">{contact?.email}</Typography>
        )}
      </TableCell>
      <TableCell>
        {isEditing ? (
          <FormikTextField
            name="title"
            size="small"
            fullWidth
            hiddenLabel
            showHelperTextInTooltip
            inputProps={{ 'aria-label': 'Title' }}
          />
        ) : (
          <Typography variant="body2">{contact?.title}</Typography>
        )}
      </TableCell>
      <TableCell>
        {isEditing ? (
          <FormikTextField
            name="notes"
            size="small"
            fullWidth
            hiddenLabel
            showHelperTextInTooltip
            inputProps={{ 'aria-label': 'Note' }}
          />
        ) : (
          <Typography variant="body2">{contact?.notes}</Typography>
        )}
      </TableCell>
      <TableCell>
        {isEditing && !isDraftRow ? (
          <FormikSelectField
            name="recordStatus"
            options={STATUS_OPTIONS}
            size="small"
            fullWidth
            hiddenLabel
            required
            inputProps={{ 'aria-label': 'Status' }}
          />
        ) : (
          <Typography variant="body2">
            {getStatusLabel(isDraftRow ? STATUS_OPTIONS[0].value : contact.recordStatus)}
          </Typography>
        )}
      </TableCell>
      {canEdit && (
        <TableCell sx={{ whiteSpace: 'nowrap' }}>
          {isEditing ? (
            <Fragment>
              <IconButton
                aria-label="Save contact"
                size="small"
                disabled={isSaving}
                onClick={onSave}
              >
                <CheckIcon fontSize="small" />
              </IconButton>
              <IconButton
                aria-label="Cancel edit"
                size="small"
                disabled={isSaving}
                onClick={onCancel}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Fragment>
          ) : (
            <Fragment>
              <IconButton
                aria-label={`Edit ${contact?.name}`}
                size="small"
                disabled={actionsDisabled}
                onClick={onEdit}
              >
                <EditIcon fontSize="small" />
              </IconButton>
              <IconButton
                aria-label={`Delete ${contact?.name}`}
                size="small"
                disabled={actionsDisabled}
                onClick={onDelete}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Fragment>
          )}
        </TableCell>
      )}
    </TableRow>
  );
}
